import Point from './point';

/**
 * Representation of a line in a two dimensional space
 *
 *     var point = new Line(new Point(125.456, 658.26645), new Point(225.456, 758.26645));
 */
export default class Line {
    /**
     * The start point
     * @property {Point}
     */
    startPoint = null;

    /**
     * The end point
     * @property {Point}
     */
    endPoint = null;

    /**
     * @param {Point} startPoint Start point
     * @param {Point} endPoint   End point
     */
    constructor(...args) {
        let [startPoint, endPoint] = args;

        if (args.length === 4) {
            const [x1, y1, x2, y2] = args;
            startPoint = new Point(x1, y1);
            endPoint = new Point(x2, y2);
        } else if (args.length !== 2) {
            throw new Error('Line expects exactly two or four parameters');
        } else if (!(startPoint instanceof Point) || !(endPoint instanceof Point)) {
            throw new Error('Line expects two point instances');
        }

        this.startPoint = startPoint;
        this.endPoint = endPoint;
    }

    /**
     * Transforms the line by the given transform matrix
     *
     * @param {Ofcs.Matrix} matrix Transform matrix
     * @return {Line}
     *
     * @chainable
     */
    transform(matrix) {
        this.startPoint.transform(matrix);
        this.endPoint.transform(matrix);
        return this;
    }

    /**
     * Return an array containing x and y
     *
     * This can be used to serialize and persist the point.
     * The name of this method is a bit confusing, because it
     * conforms to JavaScript's JSON API.
     *
     * @link https://developer.mozilla.org/en-US/docs/JSON#toJSON()_method
     * @return {Array} Array with x and y
     */
    toJSON() {
        return [this.startPoint.toJSON(), this.endPoint.toJSON()];
    }

    /**
     * Return a cloned instance of Line
     *
     * @return {Line}
     */
    clone() {
        return new Line(this.startPoint.clone(), this.endPoint.clone());
    }

    /**
     * Check if this line intersects another line.
     * Returns false if lines do not intersect, true if lines are identical and instance of
     * Point if lines cross
     *
     * @param {Line} other
     * @return {boolean|Point}
     */
    intersect(other) {
        const a1 = this.startPoint;
        const a2 = this.endPoint;
        const b1 = other.startPoint;
        const b2 = other.endPoint;
        const uaT = ((b2.x - b1.x) * (a1.y - b1.y)) - ((b2.y - b1.y) * (a1.x - b1.x));
        const ubT = ((a2.x - a1.x) * (a1.y - b1.y)) - ((a2.y - a1.y) * (a1.x - b1.x));
        let uB = ((b2.y - b1.y) * (a2.x - a1.x)) - ((b2.x - b1.x) * (a2.y - a1.y));

        if (uaT === 0 || ubT === 0) return false;

        if (uB !== 0) {
            const ua = uaT / uB;
            uB = ubT / uB;

            if (ua >= 0 && ua <= 1 && uB >= 0 && uB <= 1) {
                const point = new Point(
                    a1.x + (ua * (a2.x - a1.x)),
                    a1.y + (ua * (a2.y - a1.y)),
                );

                if (point.equal(a1) || point.equal(a2) || point.equal(b1) || point.equal(b2)) {
                    return false;
                }

                return point;
            }
        }

        return false;
    }

    /**
     * Checks if the line contains a given point
     *
     * @param {Point} point Point to check
     * @param {number} [delta] Delta precision, defaults to 0.0001
     * @return {boolean} True if point is on line
     */
    containsPoint(point, delta = 1e-4) {
        const start = this.startPoint;
        const end = this.endPoint;

        // ensure points are collinear
        const zero = ((end.x - start.x) * (point.y - start.y)) - ((point.x - start.x) * (end.y - start.y));
        if (zero > delta || zero < -delta) return false;

        // check if x-coordinates are not equal
        if (start.x - end.x > delta || end.x - start.x > delta) {
        // ensure x is between a.x & b.x (use tolerance)
            return start.x > end.x
                ? point.x + delta > end.x && point.x - delta < start.x
                : point.x + delta > start.x && point.x - delta < end.x;
        }

        // ensure y is between a.y & b.y (use tolerance)
        return start.y > end.y
            ? point.y + delta > end.y && point.y - delta < start.y
            : point.y + delta > start.y && point.y - delta < end.y;
    }

    /**
     * Check if this line intersects another line.
     * Returns false if lines are identical or object with coordinates
     * for intersection or intersection projection.
     * Lines intersect if first && second are true else the projected point intersects with
     * the line that is set to true.
     *
     * @param {Line} other
     * @return {boolean|Object}
     */
    projectIntersection(other) {
        const a1 = this.startPoint;
        const a2 = this.endPoint;
        const b1 = other.startPoint;
        const b2 = other.endPoint;
        const uaT = ((b2.x - b1.x) * (a1.y - b1.y)) - ((b2.y - b1.y) * (a1.x - b1.x));
        const ubT = ((a2.x - a1.x) * (a1.y - b1.y)) - ((a2.y - a1.y) * (a1.x - b1.x));
        const uB = ((b2.y - b1.y) * (a2.x - a1.x)) - ((b2.x - b1.x) * (a2.y - a1.y));

        const result = {
            x: null,
            y: null,
            first: false,
            second: false,
        };

        if (uaT === 0 || ubT === 0) {
            return false;
        }

        if (uB !== 0) {
            const ua = uaT / uB;
            const ub = ubT / uB;

            if (ua >= 0 && ua <= 1) {
                result.first = true;
            }
            if (ub >= 0 && ub <= 1) {
                result.second = true;
            }

            result.x = a1.x + (ua * (a2.x - a1.x));
            result.y = a1.y + (ua * (a2.y - a1.y));
        }

        return result;
    }

    /**
     * tells if another given line has the same slope
     * as this line
     *
     * @param {Line} line
     * @return {boolean}
     */
    hasSameSlope(line) {
        const slope1 = (this.startPoint.y - this.endPoint.y) / (this.startPoint.x - this.endPoint.x);
        const slope2 = (line.startPoint.y - line.endPoint.y) / (line.startPoint.x - line.endPoint.x);

        return slope1 === slope2;
    }

    /**
     * calculates angle for given line
     * @return {number}
     */
    getAngle() {
        const dy = this.endPoint.y - this.startPoint.y;
        const dx = this.endPoint.x - this.startPoint.x;

        if (dx === 0) {
            if (dy > 0) return 90;
            return 270;
        }

        let temp = Math.atan2(dy, dx); // range (-PI, PI]
        temp *= 180 / Math.PI; // rads to degs, range (-180, 180]
        if (temp < 0) temp = 360 + temp; // range [0, 360)
        return temp;
    }

    /**
     * Calculates angle between given and other line
     * @param {Line} other - other line
     * @return {number}
     */
    getAngleToLine(other) {
        const a1 = this.getAngle();
        const a2 = other.getAngle();
        const result = Math.abs(a1 - a2);

        return result < 180 ? Math.abs(result - 180) : result - 180;
    }

    /**
     * Projects new endPoint in a given distance
     * for a given angle
     * @param {number} angle
     * @return {Point}
     */
    projectToAngle(angle) {
        return this.startPoint.moveByAngle(angle, 10);
    }

    /**
     * Returns center point of the line
     *
     * @return {Point} Center of line
     */
    getCenter() {
        return new Point(
            (this.startPoint.x + this.endPoint.x) / 2,
            (this.startPoint.y + this.endPoint.y) / 2,
        );
    }

    getLength() {
        return this.startPoint.getDistanceTo(this.endPoint);
    }

    /**
     * This method checks if the given point would split the line into two smaller segments and returns and array of
     * those segment lines with exactly 2 entries. The first entry is the segment closer to the startPoint of the
     * original line. The second entry is closer to the endpoint. An entry can be null if the whole line would be part
     * of the other segment.
     *
     * @param {Point} splitPoint
     * @return {Line[]}
     * @private
     */
    split(splitPoint) {
        const { startPoint, endPoint } = this;
        if (startPoint.equal(splitPoint)) return [null, this];
        if (endPoint.equal(splitPoint)) return [this, null];

        return [new Line(startPoint, splitPoint), new Line(splitPoint, endPoint)];
    }
}
